import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { Form, Button, Modal } from "react-bootstrap";
import { SiweMessage } from "siwe";
import { chain, kyc } from "../../config";
import { getExpirationDate } from "../../helpers/date_helper";
import Loader from "react-js-loader";
import { connect, useDispatch } from "react-redux";
import { withTranslation } from "react-i18next";
import { STORE_USER_KYC, UPDATE_KYC_FORM, SET_KYC_STEP } from "../../store/user/actionTypes";
import { selectUserAddress, selectKycProcess, selectKycSignature } from "../../store/user/selectors";
import { selectCurrent } from "../../store/eTokens/selectors";

const QUESTION_SECTIONS = {
  financialLiteracy: [
    {
      id: "q1",
      label: "What does “diversification” mean in investing?",
      options: [
        "Putting all your money into one type of investment.",
        "Spreading your money across different types of investments.",
        "Only investing in the most popular stocks.",
        "Investing only in risk-free assets.",
      ],
    },
    {
      id: "q2",
      label: "What is the primary benefit of diversification?",
      options: [
        "Higher guaranteed returns.",
        "Lowering the overall risk of the investment portfolio.",
        "Avoiding taxes on investments.",
        "Doubling the investment capital.",
      ],
    },
    {
      id: "q3",
      label: "Which of the following is the most liquid asset?",
      options: ["A house.", "A car", "Cash in a saving accounts.", "Gold jewelry,"],
    },
    {
      id: "q4",
      label: "What does compound interest mean?",
      options: [
        "Earning interest only on the initial amount of money invested.",
        "Earning interest on both the initial investment and previously earned interest.",
        "Losing money due to inflation.",
        "A special type of interest applied only to large investments.",
      ],
    },
    {
      id: "q5",
      label: "Why is it important to evaluate the liquidity of an investment?",
      options: [
        "To determine how quickly you can access your money when needed.",
        "To predict how much interest the investment will earn.",
        "To understand the level of risk in the investment.",
        "To reduce investment fees.",
      ],
    },
    {
      id: "q6",
      label: "Which of the following is considered a “stable” investment?",
      options: ["Cryptocurrencies.", "Savings accounts.", "Venture capital funds.", "Technology stocks."],
    },
    {
      id: "q7",
      label: "What happens if the value of an investment drops below what you paid for it?",
      options: [
        "You will lose money if you sell it.",
        "You will automatically make a profit.",
        "The investment becomes worthless.",
        "The investment is guaranteed to recover in value.",
      ],
    },
    {
      id: "q8",
      label: "Why is blockchain technology important in decentralized finance (DeFi)?",
      options: [
        "It allows transactions to be stored securely and transparently.",
        "It guarantees high returns for investors.",
        "It eliminates all risks in investments.",
        "It replaces the need for a financial advisor.",
      ],
    },
    {
      id: "q9",
      label: "Which of the following is a stablecoin used by Ensuro?",
      options: ["Bitcoin (BTC).", "Ethereum (ETH).", "USD Coin (USDC).", "Polygon (MATIC)"],
    },
    {
      id: "q10",
      label: "What does it mean if an investment is “overcollateralized” in DeFi?",
      options: [
        "Overcollateralization in DeFi lending refers to the practice of depositing collateral that exceeds the value of the borrowed amount.",
        "The investment guarantees zero losses.",
        "The investment relies on multiple smart contracts.",
        "The investment can only lose 50% of its value.",
      ],
    },
    {
      id: "q11",
      label: "What is the main goal of Ensuro's liquidity pools?",
      options: [
        "To store cryptocurrencies for future use.",
        "To provide solvency capital for insurance risks.",
        "To generate speculative profits.",
        "To allow trading of assets.",
      ],
    },
    {
      id: "q12",
      label: "Why is it important for non-accredited investors to have investment limits?",
      options: [
        "To maximize their earnings.",
        "To ensure they don’t lose more than they can afford.",
        "To meet government regulations.",
        "To increase the market value of their investments.",
      ],
    },
  ],

  riskComprehension: [
    {
      id: "q13",
      label: "What does “market volatility” mean?",
      options: [
        "The market is predictable.",
        "The market’s value fluctuates rapidly.",
        "The market is declining steadily.",
        "The market is increasing steadily.",
      ],
    },
    {
      id: "q14",
      label: "What is the risk of investing in high-return opportunities?",
      options: [
        "There is no risk in high-return investments.",
        "They are guaranteed to double your money.",
        "They often come with a high chance of loss.",
        "They are easy to predict.",
      ],
    },
    {
      id: "q15",
      label: "In DeFi, what is the primary risk of smart contracts?",
      options: [
        "High-interest rates.",
        "Technical bugs or vulnerabilities.",
        "Guaranteed losses on all investments.",
        "Lack of investment options.",
      ],
    },
    {
      id: "q16",
      label: "Why should investors be cautious of promises of “guaranteed high returns”?",
      options: [
        "High returns are always risk-free.",
        "Guarantees are common in investment markets.",
        "Such claims are often unrealistic or fraudulent.",
        "Investments never involve risk.",
      ],
    },
    {
      id: "q17",
      label: "What is an insurance-linked investment?",
      options: [
        "An investment directly tied to life insurance policies.",
        "A product where returns depend on underwriting insurance risks.",
        "A savings account offered by insurance companies.",
        "A loan provided by an insurance firm.",
      ],
    },
    {
      id: "q18",
      label: "What is Ensuro's approach to reducing risks for its investors?",
      options: [
        "Providing guarantees for 100% returns.",
        "Using diversified liquidity pools and real-time risk monitoring.",
        "Eliminating Know Your Customer (KYC) requirements.",
        "Investing in volatile cryptocurrencies.",
      ],
    },
    {
      id: "q19",
      label: "What happens if you lose control of your private keys?",
      options: [
        "Ensuro can help you recover them.",
        "You can request a refund.",
        "Loss of private keys will permanently deny you access to your funds.",
        "You can reset your keys through customer support.",
      ],
    },
    {
      id: "q20",
      label: "What type of risk remains even though Ensuro uses audited DeFi protocols?",
      options: [
        "No risk exists after an audit.",
        "DeFi protocols can still have smart contract vulnerabilities.",
        "DeFi protocols guarantee profits after audit.",
        "Audits remove all liquidity risks.",
      ],
    },
  ],

  accountingAndTax: [
    {
      id: "q21",
      label: "In most jurisdictions, how is interest income from eToken holdings typically taxed?",
      options: [
        "It is not taxed",
        "It is taxed at capital gains rates.",
        "It is taxed as ordinary income.",
        "It is only taxed after 5 years.",
      ],
    },
    {
      id: "q22",
      label: "What is generally required for accurate tax reporting on eToken transactions?",
      options: [
        "Only the transaction amount",
        "Full transaction records including dates, values, and transaction hashes.",
        "A verbal agreement with the tax office.",
        "No documentation is needed.",
      ],
    },
  ],

  cybersecurity: [
    {
      id: "q23",
      label: "What is considered the most secure way to store private keys for significant eToken holdings?",
      options: [
        "In an email account",
        "On a piece of paper taped to your computer",
        "In a hardware wallet",
        "In a mobile app wallet",
      ],
    },
    {
      id: "q24",
      label: "Which of the following is a common sign of a phishing attempt?",
      options: [
        "Receiving an email asking for your wallet seed phrase.",
        "Receiving official updates from Ensuro Blog.",
        "Using your bookmarked official Ensuro website.",
        "Verifying your hardware wallet connection.",
      ],
    },
  ],
};

const pickRandom = (arr, n) => {
  const copy = [...arr];
  for (let i = copy.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [copy[i], copy[j]] = [copy[j], copy[i]];
  }
  return copy.slice(0, n);
};

const getRandomQuestions = () => {
  return [
    ...pickRandom(QUESTION_SECTIONS.financialLiteracy, 2),
    ...pickRandom(QUESTION_SECTIONS.riskComprehension, 2),
    ...pickRandom(QUESTION_SECTIONS.accountingAndTax, 1),
    ...pickRandom(QUESTION_SECTIONS.cybersecurity, 1),
  ];
};

const StepQuestionnaire = ({ t, kycAnswers, userAddress, kycSignature, email, etkWhitelist }) => {
  const dispatch = useDispatch();
  const questions = kycAnswers.selectedQuestions || [];
  const [step, setStep] = useState(kycAnswers.kycQuestionStep || 1);
  const currentQuestion = questions[step - 1];

  const handleChange = (questionId, value) => {
    dispatch({
      type: UPDATE_KYC_FORM,
      payload: {
        literacyTestAnswers: {
          ...kycAnswers.literacyTestAnswers,
          [questionId]: value,
        },
      },
    });
  };

  const isStepComplete = currentQuestion && kycAnswers.literacyTestAnswers?.hasOwnProperty(currentQuestion.id);

  const handleNextStep = () => {
    if (step < questions.length) {
      const newStep = step + 1;
      setStep(newStep);
      dispatch({
        type: UPDATE_KYC_FORM,
        payload: {
          kycQuestionStep: newStep,
        },
      });
    }
  };

  const getStatement = () => {
    const answersSummary = Object.entries(kycAnswers.literacyTestAnswers || {})
      .map(([key, value]) => `${key}:${value}`)
      .join(",");
    return `Email address: ${email}. I accept the Ensuro Terms of Service. I am aware of the Cyber security considerations. I am aware of the Tax implications. I am aware ot the Risk Disclosures. See resources below. Investor Test Answers: ${answersSummary}`;
  };

  const message =
    userAddress &&
    new SiweMessage({
      domain: window.location.host,
      address: userAddress,
      statement: getStatement(),
      uri: window.location.origin,
      version: "1",
      chainId: chain.id,
      expirationTime: getExpirationDate(),
      resources: [
        `${kyc.tos.url}?hash=${kyc.tos.hash}&last_updated=${kyc.tos.last_updated}`,
        `${kyc.cyber.url}?hash=${kyc.cyber.hash}&last_updated=${kyc.cyber.last_updated}`,
        `${kyc.tax.url}?hash=${kyc.tax.hash}&last_updated=${kyc.tax.last_updated}`,
        `${kyc.risk.url}?hash=${kyc.risk.hash}&last_updated=${kyc.risk.last_updated}`,
      ],
    });

  const signMessage = async () => {
    try {
      let args = { email: email, whitelist: etkWhitelist };
      const m = message.prepareMessage();
      dispatch({ type: STORE_USER_KYC, key: "ensuroTerms", userAddress: userAddress, args: args });
      dispatch({ type: "ETH_PLAIN_SIGN", key: "ensuroTerms", message: m, userAddress: userAddress });
    } catch (addError) {
      throw addError;
    }
  };

  useEffect(() => {
    if (!kycAnswers.selectedQuestions?.length) {
      const q = getRandomQuestions();
      dispatch({
        type: UPDATE_KYC_FORM,
        payload: {
          selectedQuestions: q,
          kycQuestionStep: 1,
        },
      });
    }
  }, [dispatch, kycAnswers.selectedQuestions]);

  useEffect(() => {
    if (kycSignature) {
      dispatch({ type: SET_KYC_STEP, step: "idenfy" });
    }
  }, [kycSignature, dispatch]);

  if (!questions || !questions.length || !questions[step - 1]) {
    return <Loader type="spinner-default" height={50} width={100} timeout={1000} />;
  }

  return (
    <>
      <Form>
        <Form.Group key={currentQuestion.id} className="mb-3" controlId={currentQuestion.id}>
          <Form.Label>{t(currentQuestion.label)}</Form.Label>
          {currentQuestion.options.map((option, idx) => (
            <Form.Check
              key={idx}
              type="radio"
              label={t(option)}
              name={`radio-${currentQuestion.id}`}
              id={`radio-${currentQuestion.id}-${idx}`}
              checked={kycAnswers.literacyTestAnswers?.[currentQuestion.id] === idx}
              onChange={() => handleChange(currentQuestion.id, idx)}
            />
          ))}
        </Form.Group>
      </Form>
      <Modal.Footer>
        {step < questions.length ? (
          <Button variant="primary" disabled={!isStepComplete} onClick={handleNextStep}>
            {t("Next")}
          </Button>
        ) : (
          <Button variant="primary" disabled={!isStepComplete} onClick={signMessage}>
            {t("Sign In")}
          </Button>
        )}
      </Modal.Footer>
    </>
  );
};

StepQuestionnaire.propTypes = {
  t: PropTypes.func.isRequired,
};

const mapStateToProps = (state) => {
  const userAddress = selectUserAddress(state.UserReducer);
  const canTransact = state.UserReducer.canTransact;
  const etk = selectCurrent(state.ETokensReducer);
  const etkWhitelist = etk && etk.whitelist;
  const kycProcess = selectKycProcess(state.UserReducer);
  const { email } = selectKycProcess(state.UserReducer);
  const kycSignature = selectKycSignature(state.UserReducer);
  return {
    userAddress,
    canTransact,
    etkWhitelist,
    kycAnswers: kycProcess,
    email,
    kycSignature,
  };
};

export default connect(mapStateToProps)(withTranslation()(StepQuestionnaire));
